import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { classifySetPasswordError } from '../../lib/authErrors';
import { AuthLayout } from './AuthLayout';
import { SetPasswordForm } from './SetPasswordForm';

/**
 * Accept an invitation: choose the password that creates the account's first
 * credential. A password never travels over email — the invite carries a
 * single-use token, and the user supplies the secret themselves.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS PAGE FIRES NOTHING ON MOUNT. NOT A VALIDATION CALL, NOT A "IS THIS TOKEN
 * STILL GOOD" PROBE. NOTHING. DO NOT ADD ONE.
 *
 * Invite tokens live 72 hours so they survive a weekend, and they are strictly
 * single-use — which is only safe because a corporate link scanner (Outlook
 * Safe Links, Proofpoint) that detonates the URL headlessly gets a page and
 * nothing else. A GET consumes nothing. The token is spent by the POST below,
 * which carries a password no scanner can produce. Registered as §A5.
 *
 * `VerifyPage.tsx` exchanges on mount. That is the pattern this page exists to
 * NOT be.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(password: string) {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      // `SetPasswordResponse` IS `SessionResponse` (shared contract) — the
      // endpoint always mints a session, so there is no "now go and sign in"
      // path to write. Accepting an invite is the one moment where signing
      // someone straight in is obviously right: they just proved they hold the
      // inbox AND chose the secret.
      const session = await api.acceptInvite(token, password);
      await setSession(session.sessionToken);
      navigate('/', { replace: true });
    } catch (err) {
      setError(classifySetPasswordError(err, 'invite'));
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        title="This link is incomplete"
        subtitle="Coffman Excavation · Time & Materials portal"
      >
        <div className="auth-note">
          The invitation link is missing its token — email clients sometimes break long links across
          lines. Ask your project administrator to send a new invitation, and open it in a single
          click.
        </div>
        <div className="auth-links">
          <Link className="btn-link" to="/login">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set your password" subtitle="Coffman Excavation · Time & Materials portal">
      <SetPasswordForm
        email={email}
        submitLabel="Create account"
        busyLabel="Saving…"
        busy={busy}
        error={error}
        onSubmit={(password) => void submit(password)}
      />
    </AuthLayout>
  );
}
